import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, ArrowUpRight, ArrowLeft, KeyRound, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { withRef } from '../lib/affiliate';
import { getOffer, UPSELL_TIERS } from '../lib/offers';

// Dedicated upsell sub-landing for a single OTO tier (linked from Pricing).
export default function Upsell() {
  const { tier } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState({});
  useEffect(() => { api.publicConfig().then(setConfig).catch(() => {}); window.scrollTo(0, 0); }, [tier]);

  const offer = getOffer(tier, config);

  if (!offer) {
    return (
      <div className="App min-h-screen bg-background text-foreground">
        <Navbar />
        <main className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center justify-center px-5 text-center" data-testid="upsell-not-found">
          <h1 className="font-serif-display text-3xl text-white">Offer not found</h1>
          <p className="mt-3 text-sm text-muted-foreground">This upgrade doesn&apos;t exist.</p>
          <Button onClick={() => navigate('/pricing')} className="mt-6 rounded-full bg-white px-6 text-black hover:bg-white/90">See all offers</Button>
        </main>
        <Footer />
      </div>
    );
  }

  const link = (config.buy_links || {})[tier];
  const buy = () => {
    if (link) window.open(withRef(link), '_blank', 'noopener');
    else navigate('/register');
  };

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Navbar />

      <section className="relative overflow-hidden pt-32" data-testid={`upsell-${tier}`}>
        <div className="pointer-events-none absolute left-1/2 top-24 h-[360px] w-[640px] -translate-x-1/2 rounded-full opacity-25 blur-[120px]" style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.6), transparent 70%)' }} />
        <div className="relative mx-auto max-w-3xl px-5 pb-16 text-center md:px-8">
          <button onClick={() => navigate('/pricing')} className="mx-auto mb-6 inline-flex items-center gap-1.5 text-xs text-white/50 transition-colors hover:text-white" data-testid="upsell-back">
            <ArrowLeft size={13} /> All offers
          </button>
          <div className="inline-flex items-center gap-2 rounded-full border border-node-model/30 bg-node-model/10 px-3.5 py-1.5 text-xs text-node-model">
            <Sparkles size={13} /> Upgrade &middot; {offer.name}
          </div>
          <h1 className="mt-6 font-serif-display text-4xl leading-[1.05] text-white sm:text-5xl md:text-6xl">{offer.tagline}</h1>
          <div className="mt-6 flex items-baseline justify-center gap-2">
            <span className="font-serif-display text-5xl text-white">{offer.price}</span>
            <span className="text-sm text-muted-foreground">{offer.cadence}</span>
          </div>
          <div className="mt-8 flex justify-center">
            <button onClick={buy} data-testid={`upsell-buy-${tier}`} className="group inline-flex h-12 items-center gap-1.5 rounded-full bg-white px-8 text-sm font-medium text-black transition-colors hover:bg-white/90">
              {link ? `Add ${offer.name} for ${offer.price}` : 'Get started'} <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
          <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-white/45"><KeyRound size={13} /> One-time payment &middot; Bring your own keys &middot; No monthly fees</p>
        </div>
      </section>

      <section className="relative border-y border-border bg-[#0B0B0D]">
        <div className="mx-auto max-w-3xl px-5 py-16 md:px-8">
          <h2 className="text-center font-serif-display text-2xl text-white sm:text-3xl">Everything you unlock</h2>
          <ul className="mx-auto mt-8 grid max-w-xl gap-3 sm:grid-cols-2">
            {offer.features.map((f) => (
              <li key={f} className="flex items-start gap-2.5 rounded-xl border border-border bg-card p-4 text-sm text-white/85" data-testid={`upsell-feature-${tier}`}>
                <Check size={16} className="mt-0.5 shrink-0 text-node-environment" />{f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="relative mx-auto max-w-5xl px-5 py-16 md:px-8">
        <p className="text-center text-sm font-medium uppercase tracking-widest text-node-style">Keep building</p>
        <h2 className="mt-3 text-center font-serif-display text-2xl text-white sm:text-3xl">Other upgrades</h2>
        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          {UPSELL_TIERS.filter((t) => t !== tier).map((t) => {
            const o = getOffer(t, config);
            return (
              <button key={t} onClick={() => navigate(`/upgrade/${t}`)} data-testid={`upsell-other-${t}`}
                className="group rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-white/25">
                <p className="text-sm font-semibold text-white">{o.name}</p>
                <p className="mt-1 font-serif-display text-2xl text-white">{o.price}</p>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground line-clamp-2">{o.tagline}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-node-style">View offer <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </button>
            );
          })}
        </div>
      </section>

      <Footer />
    </div>
  );
}
